import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { debounce } from "throttle-debounce";
import type { Note } from "@/services/api";
import type { NoteEditorFormValues } from "../types";
import { getNoteEditorSchema } from "../schema";

export const useEditorForm = (
  currentNote: Note,
  onSubmit: (nextNote: NoteEditorFormValues["currentNote"]) => void,
) => {
  const formApi = useForm({
    resolver: yupResolver(getNoteEditorSchema()),
    values: {
      currentNote: { ...currentNote, personId: currentNote?.personId || null },
    },
    shouldFocusError: false,
    reValidateMode: "onBlur",
  });

  const submit = useCallback(
    debounce(2000, async (nextNote: NoteEditorFormValues["currentNote"]) => {
      if (!nextNote) return;

      const isValid = await formApi.trigger();

      if (!isValid) return;

      onSubmit(nextNote);
    }),
    [onSubmit],
  );

  const { handleSubmit, watch, getValues } = formApi;

  const noteType = watch("currentNote.noteType");

  useEffect(() => {
    const subscription = watch(() => {
      const currentValues = getValues();
      const initialValues = {
        currentNote: { ...currentNote, personId: currentNote?.personId || null },
      };

      if (JSON.stringify(currentValues) !== JSON.stringify(initialValues)) {
        return handleSubmit((data) => {
          return submit(data.currentNote);
        })();
      }
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch, getValues, currentNote]);

  return { formApi, noteType };
};
